/** Display-only projection of utility statements; never infers site capacity. */
export function waterServiceField(path: string, value: unknown): {
  label: string;
  displayValue: string;
  isEmpty: boolean;
  provenance: 'doc' | 'authored' | 'unknown';
  sourceLabel?: string;
} | undefined {
  const supply = path === 'waterResource.municipalSupply';
  if (!supply && path !== 'waterResource.wastewaterService') return undefined;
  const data = value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
  const text = (v: unknown): string | undefined => typeof v === 'string'
    && v.trim() && !/^(unknown|n\/a|tbd|not available)$/i.test(v.trim()) ? v.trim() : undefined;
  const parts: string[] = [];
  const provider = text(data.provider);
  if (provider) parts.push(provider);
  const description = text(data.sourceDescription);
  if (description) parts.push(description);
  for (const [key, label, unit] of [
    ['avgMgd', 'Average', 'MGD'], ['peakMgd', 'Peak', 'MGD'],
    ['permittedMgd', 'Permitted', 'MGD'], ['excessMgd', 'Reported spare capacity', 'MGD'],
    ['storageGallons', 'Storage', 'gal'], ['nearestMainMiles', 'Nearest connection', 'mi'],
  ] as const) {
    const number = data[key];
    if (typeof number === 'number' && Number.isFinite(number) && number >= 0) {
      parts.push(`${label}: ${number.toLocaleString('en-US', { maximumSignificantDigits: 6 })} ${unit}`);
    }
  }
  if (data.lineToSite === true) parts.push('Line reaches site');
  if (data.lineToSite === false) parts.push('No line to site');
  if (data.connectionType === 'gravity') parts.push('Gravity connection');
  if (data.connectionType === 'force-main') parts.push('Force-main connection');
  const isEmpty = parts.length === 0;
  const attribution = data.provenance !== null && typeof data.provenance === 'object'
    ? data.provenance as Record<string, unknown> : {};
  const document = text(attribution.documentName) ?? text(attribution.documentId);
  const claim = text(attribution.claimId);
  const provenance = isEmpty ? 'unknown' : document ? 'doc' : claim ? 'authored' : 'unknown';
  if (!isEmpty && attribution.appliesToProject === 'other-project') parts.push('For another project; site service unconfirmed');
  else if (!isEmpty) parts.push('Site allocation unconfirmed');
  return {
    label: supply ? 'Municipal water supply' : 'Wastewater service',
    displayValue: isEmpty ? 'Unknown — no service figures recorded' : parts.join(' · '),
    isEmpty,
    provenance,
    sourceLabel: document ?? (claim ? 'Authored utility statement' : undefined),
  };
}
